import { isPositiveStatus } from "../../utils";
import {
  HealthData,
  HealthIssue,
  OverallSeverity,
  StatusItem,
} from "./types";

export const flattenSqsQueues = (
  entries?: Record<string, StatusItem>[],
): (StatusItem & { key: string })[] =>
  (entries || []).map((entry) => {
    const [key, item] = Object.entries(entry)[0] || [];
    return { key, ...(item || {}) };
  });

export const collectHealthIssues = (health: HealthData): HealthIssue[] => {
  const issues: HealthIssue[] = [];

  const scanGroup = (group: string, items?: Record<string, StatusItem>) => {
    Object.values(items || {}).forEach((item) => {
      if (!isPositiveStatus(item.status)) {
        issues.push({
          group,
          label: item.ui_display_name || "Unknown",
          detail: item.error || item.status,
        });
      }
    });
  };

  scanGroup("Core Services", health.services);
  scanGroup("EKS Services", health.eks_services);

  const { auth0, rest: otherAwsServices } = splitAuth0(health.aws_services);
  scanGroup("AWS Services", otherAwsServices);

  if (auth0 && !isPositiveStatus(auth0.status)) {
    issues.push({
      group: "Identity Provider",
      label: auth0.ui_display_name || "Auth0",
      detail: auth0.error || auth0.status,
    });
  }

  if (health.valkey && !isPositiveStatus(health.valkey.status)) {
    issues.push({
      group: "Cache Layer",
      label: health.valkey.ui_display_name || "Valkey",
      detail: health.valkey.error || health.valkey.status,
    });
  }

  if (health.indexing) {
    if (!isPositiveStatus(health.indexing.ping)) {
      issues.push({ group: "Indexing", label: "Ping", detail: health.indexing.ping });
    }
    if (!isPositiveStatus(health.indexing.postgres)) {
      issues.push({
        group: "Indexing",
        label: "Postgres",
        detail: health.indexing.postgres,
      });
    }
  }

  flattenSqsQueues(health.sqs_queue_status).forEach((queue) => {
    if (!isPositiveStatus(queue.status)) {
      issues.push({
        group: "SQS Queues",
        label: queue.ui_display_name || queue.key,
        detail: queue.error || queue.status,
      });
    }
  });

  (health.cloudfront || []).forEach((row) => {
    if (!row.healthy) {
      issues.push({
        group: "CDN",
        label: row.ui_display_name || row.alternate_domain_names || row.domain_name,
        detail: row.error,
      });
    }
  });

  return issues;
};

export const computeOverallSeverity = (
  issues: HealthIssue[],
): OverallSeverity => {
  if (issues.length === 0) return "operational";
  if (issues.some((issue) => issue.group === "Core Services")) return "outage";
  return "degraded";
};

export const splitAuth0 = (
  awsServices?: Record<string, StatusItem>,
): { auth0?: StatusItem; rest: Record<string, StatusItem> } => {
  const { auth0, ...rest } = awsServices || {};
  return { auth0, rest };
};

export const countHealthChecks = (health: HealthData): number =>
  Object.keys(health.services || {}).length +
  Object.keys(health.eks_services || {}).length +
  Object.keys(health.aws_services || {}).length +
  (health.valkey ? 1 : 0) +
  (health.indexing ? 2 : 0) +
  flattenSqsQueues(health.sqs_queue_status).length +
  (health.cloudfront || []).length;
